import apiClient from '@/lib/api/apiClient'
import type {
  MatchingAttempt,
  MatchingAutomationMode,
  MatchingPropertyOverview,
  MatchingPropertyRow,
  MatchingResidentRow,
  MatchingRun,
} from '../types'

function unwrap<T>(raw: unknown): T {
  let value = raw
  for (let depth = 0; depth < 4; depth += 1) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) break
    const object = value as Record<string, unknown>
    const nested = object.data ?? object.result ?? object.payload
    if (nested == null) break
    value = nested
  }
  return value as T
}

export async function fetchMatchingProperties(params?: {
  company_id?: string | number
  mode?: string
  search?: string
}): Promise<MatchingPropertyRow[]> {
  const { data } = await apiClient.get('/api/pms-resident-matching/properties', { params })
  const rows = unwrap<MatchingPropertyRow[]>(data)
  return Array.isArray(rows) ? rows : []
}

export async function updateMatchingPropertyMode(
  yardiPropertyId: number,
  matchingAutomationMode: MatchingAutomationMode,
): Promise<unknown> {
  const { data } = await apiClient.post('/api/pms-resident-matching/properties/mode', {
    yardi_property_id: yardiPropertyId,
    matching_automation_mode: matchingAutomationMode,
  })
  return unwrap(data)
}

export async function fetchMatchingOverview(propertyId: string | number): Promise<MatchingPropertyOverview> {
  const { data } = await apiClient.get(`/api/pms-resident-matching/properties/${propertyId}/overview`)
  return unwrap<MatchingPropertyOverview>(data)
}

export async function fetchMatchingResidents(
  propertyId: string | number,
  params?: { status?: string; phase?: string; search?: string },
): Promise<MatchingResidentRow[]> {
  const { data } = await apiClient.get(`/api/pms-resident-matching/properties/${propertyId}/residents`, { params })
  const rows = unwrap<MatchingResidentRow[]>(data)
  return Array.isArray(rows) ? rows : []
}

export async function fetchMatchingRuns(limit = 20): Promise<MatchingRun[]> {
  const { data } = await apiClient.get('/api/pms-resident-matching/runs', { params: { limit } })
  const rows = unwrap<MatchingRun[]>(data)
  return Array.isArray(rows) ? rows : []
}

export async function fetchMatchingRunDetail(runId: string | number): Promise<{
  run: MatchingRun
  attempts: MatchingAttempt[]
}> {
  const { data } = await apiClient.get(`/api/pms-resident-matching/runs/${runId}`)
  const result = unwrap<{ run: MatchingRun; attempts: MatchingAttempt[] }>(data)
  return {
    run: result.run,
    attempts: Array.isArray(result.attempts) ? result.attempts : [],
  }
}

export async function triggerMatchingRun(payload?: {
  property_id?: string | number
  mode?: 'SHADOW' | 'ENABLED'
}): Promise<unknown> {
  const { data } = await apiClient.post('/api/pms-resident-matching/run', payload || { mode: 'SHADOW' })
  return unwrap(data)
}
